import { useMemo } from 'react';
import {
  Checkbox,
  DataTableSkeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
} from '@carbon/react';
import { Checkmark } from '@carbon/react/icons';
import classnames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import wellStatusTypes from 'enums/well_status_types.json';
import { Date } from 'components/date';
import { Link } from 'components/link';
import type {
  DevelopmentAreaEvaluationResponse,
  EvalSlotResponse,
} from 'types/evaluation-api/responses';
import { formatNumber } from 'utils/number';
import { createWellToExcludeElement } from 'utils/wells-to-exclude';
import {
  createWellExclusionValuesAtom,
  type UnitExceptionForm,
} from './common/atoms';
import style from './unit-slots-table.module.scss';

const NULL_DISPLAY_VALUE = '--';

type ExcludedSlots =
  DevelopmentAreaEvaluationResponse['drillingAssumption']['wellsToExclude'];

interface IncludeCellCheckboxProps {
  form: UnitExceptionForm;
  slot: EvalSlotResponse;
}

/**
 * The Include checkbox: unchecking a slot adds it to the form's wells to exclude.
 *
 * @param {IncludeCellCheckboxProps} props - The form and the row's slot.
 * @return {JSX.Element} The checkbox.
 */
function IncludeCellCheckbox({ form, slot }: IncludeCellCheckboxProps) {
  const [wellExclusions, setWellExclusions] = useAtom(form.wellsToExclude);

  const wellsInfoAtom = useMemo(
    () => createWellExclusionValuesAtom(wellExclusions.value),
    [wellExclusions.value]
  );

  const wells = useAtomValue(wellsInfoAtom);

  return (
    <Checkbox
      id={`${slot.formationName}-${slot.slotNumber}`}
      checked={
        !wells.some(
          (el) =>
            el.formationName === slot.formationName &&
            el.slotNumber === slot.slotNumber
        )
      }
      onChange={(_evt, { checked }: { checked: boolean }) => {
        if (!checked) {
          setWellExclusions((el) => [...el, createWellToExcludeElement(slot)]);
        } else {
          setWellExclusions((el, get) => {
            const removeElement = el.findIndex(
              (el) =>
                get(el.formationName).value === slot.formationName &&
                get(el.slotNumber).value === slot.slotNumber
            );
            return [
              ...el.slice(0, removeElement),
              ...el.slice(removeElement + 1),
            ];
          });
        }
      }}
      labelText=""
    />
  );
}

interface IncludeCellLabelProps {
  slot: EvalSlotResponse;
  excludedSlots: ExcludedSlots;
}

/**
 * A checkmark when an evaluation included the slot, nothing when it excluded it.
 *
 * @param {IncludeCellLabelProps} props - The row's slot and the evaluation's excluded slots.
 * @return {JSX.Element | null} The checkmark, or nothing.
 */
function IncludeCellLabel({ slot, excludedSlots }: IncludeCellLabelProps) {
  const excluded = excludedSlots.some(
    (el) =>
      el.formationName === slot.formationName &&
      el.slotNumber === slot.slotNumber
  );

  return excluded ? null : <Checkmark aria-label="Included" />;
}

/**
 * A date cell, or the null placeholder when there is no date.
 *
 * @param {{ date: string | null }} props - The date.
 * @return {JSX.Element} The date or the placeholder.
 */
function DateCell({ date }: { date: string | null }) {
  return date ? <Date date={date} /> : <>{NULL_DISPLAY_VALUE}</>;
}

interface UnitSlotsTableProps {
  /** Scenario exceptions: each slot gets an Include checkbox bound to this form. */
  form?: UnitExceptionForm;
  excludedSlots?: ExcludedSlots;
  evalSlots?: DevelopmentAreaEvaluationResponse['slots'];
  devAreaSlots?: EvalSlotResponse[];
}

/**
 * A unit's slots. With a form (scenario exceptions) each row has an Include checkbox over
 * `devAreaSlots`; without one (package evaluation results) it lists `evalSlots` and marks
 * the ones the evaluation included.
 *
 * @param {UnitSlotsTableProps} props - The form or the evaluation's slots and exclusions.
 * @return {JSX.Element} The table, or a skeleton while the slots load.
 */
export function UnitSlotsTable({
  form,
  excludedSlots,
  evalSlots,
  devAreaSlots,
}: UnitSlotsTableProps) {
  const slots = form ? devAreaSlots : evalSlots;

  if (!slots)
    return (
      <DataTableSkeleton
        showHeader={false}
        columnCount={14}
        showToolbar={false}
      />
    );

  return (
    <div className={style.slotsModalTableSticky}>
      <TableContainer>
        <Table aria-label="Slots" size="md">
          <TableHead className={style.stickyHeader}>
            <TableRow>
              <TableHeader scope="col">
                {form ? 'Include' : 'Included'}
              </TableHeader>
              <TableHeader scope="col">Formation</TableHeader>
              <TableHeader scope="col">Type Curve Status</TableHeader>
              <TableHeader scope="col">Slot</TableHeader>
              <TableHeader scope="col">UWI</TableHeader>
              <TableHeader scope="col">Name</TableHeader>
              <TableHeader scope="col">Operator</TableHeader>
              <TableHeader scope="col">ResCat</TableHeader>
              <TableHeader scope="col">Forecast Status</TableHeader>
              <TableHeader scope="col">Volumes Scaling Factor</TableHeader>
              <TableHeader scope="col">Permit Date</TableHeader>
              <TableHeader scope="col">Spud Date</TableHeader>
              <TableHeader scope="col">Completion Date</TableHeader>
              <TableHeader scope="col">First Production Date</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {slots.map((slot) => (
              <TableRow key={`${slot.formationName}-${slot.slotNumber}`}>
                <TableCell>
                  {form ? (
                    <IncludeCellCheckbox slot={slot} form={form} />
                  ) : (
                    <IncludeCellLabel
                      slot={slot}
                      excludedSlots={excludedSlots ?? []}
                    />
                  )}
                </TableCell>

                <TableCell>{slot.formationName.replace(/_/g, ' ')}</TableCell>
                <TableCell>
                  {slot.typeCurveStatus?.replace(/_/g, ' ') ||
                    NULL_DISPLAY_VALUE}
                </TableCell>
                <TableCell>{slot.slotNumber}</TableCell>
                <TableCell>
                  {slot.uwi ? (
                    <Link to={`/wells/${slot.uwi}`} target={'_blank'}>
                      {slot.uwi}
                    </Link>
                  ) : (
                    NULL_DISPLAY_VALUE
                  )}
                </TableCell>
                <TableCell>{slot.name || NULL_DISPLAY_VALUE}</TableCell>
                <TableCell>{slot.operator || NULL_DISPLAY_VALUE}</TableCell>
                <TableCell>
                  {wellStatusTypes[slot.status as keyof typeof wellStatusTypes]}
                </TableCell>
                <TableCell
                  className={classnames(
                    style[slot.reserveCategory],
                    style[slot.forecastStatus]
                  )}
                >
                  {slot.forecastStatus.replace(/-/g, ' ')}
                </TableCell>
                <TableCell>
                  {slot.volumesScalingFactor === null
                    ? NULL_DISPLAY_VALUE
                    : formatNumber({
                        value: slot.volumesScalingFactor,
                        minPrecision: 4,
                        maxPrecision: 4,
                      })}
                </TableCell>
                <TableCell>
                  <DateCell date={slot.permitDate} />
                </TableCell>
                <TableCell>
                  <DateCell date={slot.spudDate} />
                </TableCell>
                <TableCell>
                  <DateCell date={slot.completionDate} />
                </TableCell>
                <TableCell>
                  <DateCell date={slot.firstProductionDate} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}
